'use client';

import { useEffect, useRef } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import toast from 'react-hot-toast';
import {
  parseRecordingEvent,
  recordingActionToWindowEvent,
  type RecordingAction,
} from '@/lib/recordingEvents';
import {
  fetchRecordingStatus,
  invalidateRecordingStatusCache,
  normalizeRecordingUiState,
  startRecordingFromRoom,
  type RecordingUiState,
} from '@/lib/recording-status-client';
import {
  RECORDING_AUTO_RESTART_MESSAGE,
  RECORDING_INTERRUPTED_USER_MESSAGE,
  RECORDING_PREPARING_TOAST,
  RECORDING_STARTING_TITLE,
  RECORDING_START_FAILED_BODY,
  RECORDING_ROOM_EMPTY_ENDED_MESSAGE,
  RECORDING_SAVED_ROOM_CONTINUES_MESSAGE,
  RECORDING_SERVER_BUSY_CONTINUES_MESSAGE,
} from '@/lib/recording-ui-messages';
import { isServerTemporarilyUnavailableError } from '@/lib/recording-egress-errors';
import { showRecordingFailureToast } from '@/lib/recording-failure-toast';
import {
  announceRecordingStartFailed,
  consumeRecordingAwaitingDismissedByUser,
  markRecordingAwaitingDismissedByUser,
  shouldAnnounceRecordingStartFailure,
} from '@/lib/recording-start-failed';

const PREPARE_POLL_MS = 2000;
/** Idle curto no meio do auto-restart não deve disparar o aviso. */
const START_FAILURE_IDLE_GRACE_MS = 3500;
/** Estado active muda por data channel; poll só como fallback lento (reduz ListEgress). */
const ACTIVE_POLL_MS = 30_000;

function applyRecordingAction(
  action: RecordingAction,
  opts?: {
    queuePosition?: number;
    message?: string;
    roomName?: string;
    room?: Room | null;
    previous?: RecordingUiState;
    fromSnapshot?: boolean;
    updatedAt?: string | null;
  },
) {
  const windowEvent = recordingActionToWindowEvent(action);
  window.dispatchEvent(new CustomEvent(windowEvent));

  if (action === 'preparing') {
    window.dispatchEvent(new CustomEvent('recording:preparing'));
    const isAutoRestart = opts?.message === RECORDING_AUTO_RESTART_MESSAGE;
    toast(isAutoRestart ? RECORDING_AUTO_RESTART_MESSAGE : RECORDING_PREPARING_TOAST, {
      icon: '⏳',
      duration: isAutoRestart ? 8_000 : 5_000,
      id: isAutoRestart ? 'recording-auto-restart' : 'recording-preparing',
    });
    return;
  }

  if (action === 'starting') {
    toast(RECORDING_STARTING_TITLE, {
      icon: '⏳',
      duration: 5_000,
      id: 'recording-starting',
    });
    return;
  }

  if (action === 'started') {
    window.dispatchEvent(new CustomEvent('recording:start'));
    toast.success('Gravação iniciada', { icon: '🎬', duration: 4_000, id: 'recording-started' });
    return;
  }

  if (action === 'stopped' || action === 'cancelled') {
    if (opts?.previous === 'preparing' || opts?.previous === 'starting') {
      markRecordingAwaitingDismissedByUser();
    }
  }

  if (action === 'failed') {
    const roomName = opts?.roomName;
    const rawMsg = opts?.message || '';
    // Twirp/"no response" não é perda fatal — toast informativo, sem botão de retry agressivo.
    if (isServerTemporarilyUnavailableError(rawMsg) || rawMsg.includes('api_transient')) {
      toast(RECORDING_SERVER_BUSY_CONTINUES_MESSAGE, {
        icon: '⏳',
        duration: 8_000,
        id: 'recording-server-busy',
      });
      return;
    }
    if (
      shouldAnnounceRecordingStartFailure({
        previous: opts?.previous ?? 'idle',
        message: rawMsg,
        fromSnapshot: opts?.fromSnapshot,
        updatedAt: opts?.updatedAt,
      })
    ) {
      toast.dismiss('recording-preparing');
      toast.dismiss('recording-starting');
      toast.dismiss('recording-failed');
      invalidateRecordingStatusCache(roomName);
      announceRecordingStartFailed(RECORDING_START_FAILED_BODY);
      return;
    }
    showRecordingFailureToast({
      message: opts?.message || RECORDING_INTERRUPTED_USER_MESSAGE,
      roomName,
      onRetry:
        opts?.room || roomName
          ? () => {
              void startRecordingFromRoom(opts?.room ?? { name: roomName }).catch(() => undefined);
            }
          : undefined,
    });
    return;
  }

  if (action === 'stopped' || action === 'cancelled') {
    toast.dismiss('recording-preparing');
    const rawMsg = opts?.message || '';
    if (
      rawMsg.includes('room_empty') ||
      rawMsg.includes('room_finished') ||
      rawMsg === RECORDING_ROOM_EMPTY_ENDED_MESSAGE
    ) {
      toast(RECORDING_ROOM_EMPTY_ENDED_MESSAGE, {
        duration: 8_000,
        id: 'recording-room-empty',
      });
    }
  }
}

/** Sincroniza estado de gravação via data channel (servidor) + polling de fallback. */
export function useRecordingRealtimeSync(room: Room | null | undefined) {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleFailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStateRef = useRef<RecordingUiState>('idle');
  const roomRef = useRef(room);
  roomRef.current = room;

  const cancelIdleFailTimer = () => {
    if (idleFailTimerRef.current) {
      clearTimeout(idleFailTimerRef.current);
      idleFailTimerRef.current = null;
    }
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const syncFromStatus = (
    state: RecordingUiState,
    extra?: { queuePosition?: number; message?: string; isRecordingFile?: boolean; egressId?: string | null },
  ) => {
    const normalized = state === 'stopping' ? 'idle' : state;
    const previous = lastStateRef.current;
    if (normalized === previous) return;
    lastStateRef.current = normalized;
    if (normalized !== 'idle') cancelIdleFailTimer();

    if (normalized === 'preparing') {
      applyRecordingAction('preparing', {
        queuePosition: extra?.queuePosition,
        message: extra?.message,
        previous,
      });
    } else if (normalized === 'starting') {
      applyRecordingAction('starting', { previous });
    } else if (normalized === 'active') {
      if (extra?.isRecordingFile && extra?.egressId) {
        window.dispatchEvent(new CustomEvent('recording:start'));
        if (previous !== 'active') {
          applyRecordingAction('started');
        }
      } else {
        applyRecordingAction('starting');
      }
      const roomName = roomRef.current?.name;
      if (roomName) startPolling(roomName, ACTIVE_POLL_MS);
    } else if (normalized === 'failed') {
      applyRecordingAction('failed', {
        message: extra?.message,
        roomName: roomRef.current?.name,
        room: roomRef.current,
        previous,
      });
      stopPolling();
    } else if (normalized === 'idle' || normalized === 'cancelled') {
      const userDismissed = consumeRecordingAwaitingDismissedByUser();
      if (
        (previous === 'preparing' || previous === 'starting') &&
        normalized === 'idle' &&
        !userDismissed
      ) {
        cancelIdleFailTimer();
        idleFailTimerRef.current = setTimeout(() => {
          idleFailTimerRef.current = null;
          if (lastStateRef.current === 'idle') {
            announceRecordingStartFailed(RECORDING_START_FAILED_BODY);
          }
        }, START_FAILURE_IDLE_GRACE_MS);
      }
      if (previous === 'active' && (roomRef.current?.numParticipants ?? 0) > 0) {
        toast(RECORDING_SAVED_ROOM_CONTINUES_MESSAGE, {
          duration: 10_000,
          id: 'recording-saved-room-continues',
        });
      } else if (previous === 'active' && (roomRef.current?.numParticipants ?? 0) === 0) {
        toast(RECORDING_ROOM_EMPTY_ENDED_MESSAGE, {
          duration: 8_000,
          id: 'recording-room-empty',
        });
      }
      applyRecordingAction('stopped');
      stopPolling();
    }
  };

  const startPolling = (roomName: string, intervalMs = PREPARE_POLL_MS) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const status = await fetchRecordingStatus(roomName);
        syncFromStatus(normalizeRecordingUiState(status), {
          queuePosition: status.queuePosition,
          isRecordingFile: status.isRecordingFile,
          egressId: status.egressId,
          message:
            status.failureReason ??
            status.message ??
            (normalizeRecordingUiState(status) === 'failed'
              ? RECORDING_INTERRUPTED_USER_MESSAGE
              : undefined),
        });
      } catch {
        /* ignore transient poll errors */
      }
    }, intervalMs);
  };

  useEffect(() => {
    if (!room?.name) return;

    lastStateRef.current = 'idle';

    fetchRecordingStatus(room.name)
      .then((status) => {
        const state = normalizeRecordingUiState(status);
        if (state === 'idle' || state === 'failed' || state === 'cancelled') {
          if (state === 'failed') {
            applyRecordingAction('failed', {
              message: status.failureReason ?? status.message,
              roomName: room.name,
              room,
              previous: 'idle',
              fromSnapshot: true,
              updatedAt: status.updatedAt,
            });
          }
          lastStateRef.current = 'idle';
          return;
        }
        syncFromStatus(state, {
          queuePosition: status.queuePosition,
          isRecordingFile: status.isRecordingFile,
          egressId: status.egressId,
          message: status.failureReason ?? status.message,
        });
        if (state === 'preparing' || state === 'starting') {
          startPolling(room.name!, PREPARE_POLL_MS);
        } else if (state === 'active') {
          startPolling(room.name!, ACTIVE_POLL_MS);
        }
      })
      .catch(() => {
        /* initial sync optional */
      });

    const handleDataReceived = (payload: Uint8Array) => {
      const parsed = parseRecordingEvent(payload);
      if (!parsed) return;

      applyRecordingAction(parsed.action, {
        queuePosition: parsed.queuePosition,
        message: parsed.message,
        roomName: room.name,
        room,
        previous: lastStateRef.current,
      });

      if (parsed.action === 'preparing' || parsed.action === 'starting') {
        lastStateRef.current = parsed.action === 'preparing' ? 'preparing' : 'starting';
        startPolling(room.name!, PREPARE_POLL_MS);
      } else if (parsed.action === 'started') {
        lastStateRef.current = 'active';
        startPolling(room.name!, ACTIVE_POLL_MS);
      } else if (parsed.action === 'failed') {
        lastStateRef.current = 'failed';
        stopPolling();
      } else if (parsed.action === 'stopped' || parsed.action === 'cancelled') {
        lastStateRef.current = 'idle';
        stopPolling();
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    const onAwaiting = () => {
      if (room.name) startPolling(room.name);
    };
    window.addEventListener('recording:preparing', onAwaiting);
    window.addEventListener('recording:starting', onAwaiting);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
      window.removeEventListener('recording:preparing', onAwaiting);
      window.removeEventListener('recording:starting', onAwaiting);
      stopPolling();
      cancelIdleFailTimer();
    };
  }, [room?.name]);
}
